import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiPrinter, FiDownload, FiCalendar } from 'react-icons/fi';
import reportesContablesService from '../services/reportesContablesService';
import Loading from '../components/Loading';
import './EstadoResultadoPage.css';

const EstadoResultadoPage = () => {
  const [estadoData, setEstadoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);

  // Calcular fechas por defecto (inicio del año hasta hoy)
  const hoy = new Date();
  const inicioAnio = new Date(hoy.getFullYear(), 0, 1);

  const [fechaDesde, setFechaDesde] = useState(inicioAnio.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(hoy.toISOString().split('T')[0]);

  useEffect(() => {
    cargarEstadoResultado();
  }, []);

  const cargarEstadoResultado = async (desde = fechaDesde, hasta = fechaHasta) => {
    try {
      setLoading(true);
      const response = await reportesContablesService.getEstadoResultado(desde, hasta);
      setEstadoData(response.data);
    } catch (error) {
      console.error('Error al cargar estado de resultado:', error);
      toast.error(error.response?.data?.error || 'Error al cargar el estado de resultado');
    } finally {
      setLoading(false);
    }
  };

  const handleFechaDesdeChange = (e) => {
    const nuevaFecha = e.target.value;
    setFechaDesde(nuevaFecha);
    cargarEstadoResultado(nuevaFecha, fechaHasta);
  };

  const handleFechaHastaChange = (e) => {
    const nuevaFecha = e.target.value;
    setFechaHasta(nuevaFecha);
    cargarEstadoResultado(fechaDesde, nuevaFecha);
  };

  const handleImprimir = () => {
    window.print();
  };

  const handleExportarExcel = async () => {
    try {
      setExportando(true);
      await reportesContablesService.exportEstadoToExcel(
        estadoData,
        fechaDesde,
        fechaHasta
      );
      toast.success('Estado de resultado exportado a Excel correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      toast.error('Error al exportar a Excel');
    } finally {
      setExportando(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getIndentClass = (nivel) => {
    return `indent-${Math.min(nivel, 4)}`;
  };

  if (loading) {
    return <Loading />;
  }

  if (!estadoData) {
    return (
      <div className="estado-resultado-page">
        <div className="error-message">No se pudo cargar el estado de resultado</div>
      </div>
    );
  }

  const esUtilidad = estadoData.utilidad_neta >= 0;

  return (
    <div className="estado-resultado-page">
      {/* Header */}
      <div className="estado-header no-print">
        <div className="estado-title-section">
          <h1>Estado de Resultado</h1>
          <p className="estado-subtitle">{estadoData.cuenta.nombre}</p>
        </div>

        <div className="estado-controls">
          <div className="fecha-control">
            <FiCalendar className="icon" />
            <input
              type="date"
              value={fechaDesde}
              onChange={handleFechaDesdeChange}
              className="fecha-input"
            />
            <span className="fecha-separator">-</span>
            <input
              type="date"
              value={fechaHasta}
              onChange={handleFechaHastaChange}
              className="fecha-input"
            />
          </div>

          <button onClick={() => cargarEstadoResultado()} className="btn-refresh" title="Actualizar">
            <FiRefreshCw />
          </button>

          <button onClick={handleImprimir} className="btn-print" title="Imprimir">
            <FiPrinter />
          </button>

          <button
            onClick={handleExportarExcel}
            className="btn-download"
            disabled={exportando}
            title="Exportar a Excel"
          >
            <FiDownload />
            {exportando && <span className="spinner-small"></span>}
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div className="estado-content">
        <div className="report-header-print">
          <h1 className="report-title">ESTADO DE RESULTADO</h1>
          <h2 className="report-subtitle">{estadoData.cuenta.nombre}</h2>
          <p className="report-period">
            Del {formatDate(estadoData.periodo.desde)} al {formatDate(estadoData.periodo.hasta)}
          </p>
        </div>

        {/* KPIs */}
        <div className="kpi-section no-print-kpi">
          <div className="kpi-card">
            <div className="kpi-label">Total Ingresos</div>
            <div className="kpi-value ingresos">{formatCurrency(estadoData.total_ingresos)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Total Gastos</div>
            <div className="kpi-value gastos">{formatCurrency(estadoData.total_gastos)}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">{esUtilidad ? 'Utilidad Neta' : 'Pérdida Neta'}</div>
            <div className={`kpi-value ${esUtilidad ? 'utilidad' : 'perdida'}`}>
              {formatCurrency(estadoData.utilidad_neta)}
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Margen Neto</div>
            <div className={`kpi-value ${esUtilidad ? 'utilidad' : 'perdida'}`}>
              {estadoData.margen}%
            </div>
          </div>
        </div>

        {/* INGRESOS */}
        <div className="estado-section">
          <h3 className="section-title ingresos-title">INGRESOS</h3>
          <table className="estado-table">
            <thead>
              <tr>
                <th className="col-codigo">Código</th>
                <th className="col-nombre">Cuenta</th>
                <th className="col-monto">Monto</th>
              </tr>
            </thead>
            <tbody>
              {estadoData.ingresos.length > 0 ? (
                estadoData.ingresos.map((cuenta) => (
                  <tr key={cuenta.id}>
                    <td className="account-code">{cuenta.codigo}</td>
                    <td className={`account-name ${getIndentClass(cuenta.nivel)}`}>
                      {cuenta.nombre}
                    </td>
                    <td className="account-balance">{formatCurrency(cuenta.saldo)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="no-data">No hay movimientos de ingresos</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="total-row total-ingresos">
                <td colSpan="2" className="total-label">TOTAL INGRESOS</td>
                <td className="total-amount">{formatCurrency(estadoData.total_ingresos)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* GASTOS */}
        <div className="estado-section">
          <h3 className="section-title gastos-title">GASTOS</h3>
          <table className="estado-table">
            <thead>
              <tr>
                <th className="col-codigo">Código</th>
                <th className="col-nombre">Cuenta</th>
                <th className="col-monto">Monto</th>
              </tr>
            </thead>
            <tbody>
              {estadoData.gastos.length > 0 ? (
                estadoData.gastos.map((cuenta) => (
                  <tr key={cuenta.id}>
                    <td className="account-code">{cuenta.codigo}</td>
                    <td className={`account-name ${getIndentClass(cuenta.nivel)}`}>
                      {cuenta.nombre}
                    </td>
                    <td className="account-balance">{formatCurrency(cuenta.saldo)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="no-data">No hay movimientos de gastos</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="total-row total-gastos">
                <td colSpan="2" className="total-label">TOTAL GASTOS</td>
                <td className="total-amount">{formatCurrency(estadoData.total_gastos)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Resumen */}
        <div className="resumen-section">
          <h4>Resumen del Período</h4>
          <div className="resumen-items">
            <div className="resumen-item">
              <span className="resumen-label">Total Ingresos:</span>
              <span className="resumen-value ingresos-color">
                {formatCurrency(estadoData.total_ingresos)}
              </span>
            </div>
            <div className="resumen-item">
              <span className="resumen-label">(-) Total Gastos:</span>
              <span className="resumen-value gastos-color">
                {formatCurrency(estadoData.total_gastos)}
              </span>
            </div>
            <div className="resumen-item resumen-utilidad">
              <span className="resumen-label">
                {esUtilidad ? 'UTILIDAD NETA:' : 'PÉRDIDA NETA:'}
              </span>
              <span className={`resumen-value ${esUtilidad ? 'utilidad-color' : 'perdida-color'}`}>
                {formatCurrency(estadoData.utilidad_neta)}
                <span className="margen-badge">{estadoData.margen}%</span>
              </span>
            </div>
          </div>
        </div>

        {/* Resultado Final */}
        <div className={`resultado-final ${esUtilidad ? 'utilidad-final' : 'perdida-final'}`}>
          {esUtilidad ? (
            <>
              <span className="resultado-icon">✓</span>
              <span className="resultado-text">
                UTILIDAD DEL PERÍODO: {formatCurrency(estadoData.utilidad_neta)} ({estadoData.margen}%)
              </span>
            </>
          ) : (
            <>
              <span className="resultado-icon">✗</span>
              <span className="resultado-text">
                PÉRDIDA DEL PERÍODO: {formatCurrency(estadoData.utilidad_neta)} ({estadoData.margen}%)
              </span>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="estado-footer no-print">
        <small>Generado el {new Date().toLocaleString('es-CL')} | Sistema de Contabilidad</small>
      </div>
    </div>
  );
};

export default EstadoResultadoPage;
